import { FC, Fragment } from "react";
import { observer } from "mobx-react";
import { isFailure, isOk, isPending } from "@/common/common";
import { useLanguage, useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { Box, Container, Grid, VBox } from "@/components-v2/Box";
import { MSelect } from "@/components-v2/Select";
import { Alert } from "@/components-v2/Alert";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { Dialog } from "@/components-v2/Dialog";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { MTextInput } from "@/components-v2/TextInput";
import { MDateInput } from "@/components-v2/DateInput";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { MCheckbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { Tabs } from "@/components-v2/Tabs";
import { Button, CloseButton, IconButton, LinkButton } from "@/components-v2/Button";
import { Icon } from "@/components-v2/Icon";
import { InfoButton } from "@/components-v2/InfoButton";
import { Tag } from "@/components-v2/Tag";
import { ResizableSidepanelLayout } from "@/components-v2/ResizableSidepanelLayout";
import { Sortable, SortableList } from "@/components-v2/SortableList";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { MTextArea } from "@/components-v2/TextArea";
import { AddressDialog } from "@/companyAddress/companyAddressComponents";
import {
  AddFieldFormModel,
  CompanyCustomField,
  CompanySystemField,
  CustomFieldCompanyScope,
  CustomFieldDataType,
  CustomFieldImportIssue,
  CustomFieldNumberFormat,
  CustomFieldSelectOption,
  CustomFieldsImportResult,
  CustomFieldValue,
  systemFieldPercent,
  systemFieldTag,
  systemFieldValueParam,
  systemFieldCountMessage,
  dataTypeLabels,
  EntityDataHistoryState,
  EntityDataViewProps,
  EntityFieldPanelViewProps,
  SystemFieldLogRow,
  EntityFieldRow,
  EntityValueFormModel,
  commentMaxLength,
  contactSearchPlaceholder,
  contactWarning,
  customFieldDateValidations,
  CustomFieldDateValidation,
  dateValidationEntries,
  entityDataTabLabel,
  entityDataTabs,
  formatCustomNumber,
  numberFormatOptions,
  importOutcome,
  importOutcomeWarnings,
  importProblemLabels,
  overlapWarning,
  selectOptionLabel,
  validationIcons,
  validationLabels,
  valueDate,
  valueDateFieldLabel,
  valueDateLabels,
} from "./customFieldsDomain";
import { TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";

export const EntityDataView: FC<EntityDataViewProps> = observer((props) => (
  <FullScreenLayout
    appBar={props.appBar}
    disableAppBarShadow
    alertStore={props.alertStore}
    centerPanelContainerProps={{ overflow: "hidden" }}
    centerPanel={
      <ResizableSidepanelLayout
        containerProps={{ overflow: "hidden" }}
        centerPanel={<EntityDataMainPanel {...props} />}
        rightBaseSizePercent={45}
        rightPanel={
          props.fieldPanel && (
            <EntityFieldPanel
              panel={props.fieldPanel}
              onEditValue={props.onEditValue}
              onConfigureFields={props.onConfigureFields}
            />
          )
        }
      />
    }
  />
));

const EntityDataPageHeader: FC<EntityDataViewProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <VBox bg borderBottom paddingTop={"lg"}>
      <Container gap={"md"}>
        <Box justifyContent={"space-between"} alignItems={"center"} minHeight={"md"}>
          <h3>{t("entity-data.title")}</h3>
          <Box alignItems={"center"} gap={"lg"}>
            {props.onConfigureFields && (
              <LinkButton
                icon={"gear"}
                label={{ key: "entity-data.configure-fields" }}
                onClick={props.onConfigureFields}
              />
            )}
            {props.allowEdit &&
              (props.isImporting ? (
                <LoadingIndicator />
              ) : (
                <DropdownMenu
                  button={{
                    type: "button",
                    variant: "secondary",
                    label: { key: "excel.edit-with-excel" },
                  }}
                  items={[
                    {
                      type: "item",
                      icon: "download",
                      label: { key: "excel.download" },
                      onSelect: props.onExportExcel,
                    },
                    props.uploadConfig && {
                      type: "file-upload",
                      label: { key: "excel.upload" },
                      fileUploadConfig: props.uploadConfig,
                      onSelect: (files) => props.onUploadExcel(files),
                    },
                  ]}
                />
              ))}
          </Box>
        </Box>
        <Tabs
          tabs={entityDataTabs
            .filter((tab) => tab !== "system" || props.hasSystemFields)
            .map((tab) => ({
              id: tab,
              label: entityDataTabLabel(tab, props.accountName),
              isActive: props.tab === tab,
              onSelect: () => props.onSelectTab(tab),
            }))}
        />
      </Container>
    </VBox>
  );
});

const EntityDataMainPanel: FC<EntityDataViewProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <VBox grow overflow={"hidden"}>
      <EntityDataPageHeader {...props} />
      <VBox grow overflowY={"auto"}>
        <Container paddingY gap={"lg"}>
          <Box gap={"md"} alignItems={"start"}>
            <Box flex={"auto"} maxWidth={"sm"}>
              <MTextInput
                model={props.searchModel}
                placeholder={t("search")}
                icon={"magnifying-glass"}
              />
            </Box>
          </Box>
          {isPending(props.fieldsResult) && <LoadingIndicator />}
          {isFailure(props.fieldsResult) && <Alert type={"error"} message={t("general-error")} />}
          {isOk(props.fieldsResult) &&
            (props.rows.length === 0 ? (
              <Box fg={"subtle"}>{t("entity-data.no-fields")}</Box>
            ) : (
              <FieldsTable {...props} />
            ))}
          {props.editModel && (
            <EditValueDialog
              model={props.editModel}
              companyName={props.companyName}
              onSubmit={props.onSubmitEdit}
              onCancel={props.onCloseEdit}
            />
          )}
          {props.addressForm && (
            <AddressDialog
              form={props.addressForm}
              onCancel={props.onCloseAddressEdit}
              onSave={props.onSaveAddress}
            />
          )}
          {props.history && (
            <ValueHistoryDialog history={props.history} onClose={props.onCloseHistory} />
          )}
          {props.importResult && (
            <ImportResultDialog result={props.importResult} onClose={props.onCloseImportResult} />
          )}
        </Container>
      </VBox>
    </VBox>
  );
});

export const FieldValue: FC<{
  value?: CustomFieldValue;
  selectOptions?: CustomFieldSelectOption[];
  numberFormat?: CustomFieldNumberFormat;
}> = observer(({ value, selectOptions = [], numberFormat }) => {
  const { t } = useTranslation();
  const { formatDate, formatNumber } = useLocaleFormat();
  if (!value) {
    return <Box fg={"subtle"}>{t("entity-data.not-set")}</Box>;
  }
  switch (value.valueType) {
    case "TEXT":
      return <>{value.text}</>;
    case "NUMBER":
      return <>{formatNumber(value.number, numberFormat && numberFormatOptions(numberFormat))}</>;
    case "DATE":
      return <>{formatDate(value.date)}</>;
    case "SINGLE_SELECT":
      return <>{selectOptionLabel(selectOptions, value.optionId) ?? "–"}</>;
    case "COUNTRY_CODE":
      return <>{t({ type: "country", code: value.code })}</>;
    case "CONTACT":
      return <>{value.name}</>;
  }
});

const ValueDate: FC<{ date?: string }> = observer(({ date }) => {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  return date ? <>{formatDate(date)}</> : <Box fg={"subtle"}>{t("entity-data.not-set")}</Box>;
});

export const SystemFieldValue: FC<{ field: CompanySystemField }> = observer(({ field }) => {
  const { t } = useTranslation();
  const locale = useLanguage();
  const value = field.value;
  const tag = systemFieldTag(field, locale);
  if (tag) {
    return <Tag {...tag} />;
  }
  const label = field.valueLabel?.[locale];
  if (label) {
    return <>{label}</>;
  }
  if (!value && field.isNotApplicable) {
    return <Box fg={"subtle"}>{t("settings.company.not-applicable")}</Box>;
  }
  const param = value?.valueType === "TEXT" ? systemFieldValueParam[field.systemField] : undefined;
  if (param === "country" && value?.valueType === "TEXT") {
    return <>{t({ type: "country", code: value.text })}</>;
  }
  if (param === "org-no" && value?.valueType === "TEXT") {
    return <>{t({ type: "org-no", value: value.text })}</>;
  }
  const count = systemFieldCountMessage(field);
  if (count) {
    return <>{t(count)}</>;
  }
  const percent = systemFieldPercent(field);
  if (percent) {
    return <>{t(percent)}</>;
  }
  return <FieldValue value={field.value} />;
});

const RowValue: FC<{ row: EntityFieldRow }> = observer(({ row }) =>
  row.origin === "SYSTEM" ? (
    <SystemFieldValue field={row.field} />
  ) : (
    <FieldValue
      value={row.field.value}
      selectOptions={row.field.selectOptions}
      numberFormat={row.field.numberFormat}
    />
  ),
);

const RowDate: FC<{ row: EntityFieldRow; validation: CustomFieldDateValidation }> = observer(
  ({ row, validation }) => {
    const { t } = useTranslation();
    if (row.origin === "SYSTEM" || !row.field.validations?.[validation]) {
      return <Box fg={"subtle"}>{t("not-applicable")}</Box>;
    }
    return <ValueDate date={valueDate(row.field, validation)} />;
  },
);

const capturesValueDates = (rows: EntityFieldRow[]): boolean =>
  rows.some(
    (row) => row.origin === "CUSTOM" && dateValidationEntries(row.field.validations).length > 0,
  );

const FieldsTable: FC<EntityDataViewProps> = observer((props) => {
  const { rows, fieldPanel, onSelectField, onEditValue, onShowHistory } = props;
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  const columns: TableColumn<EntityFieldRow>[] = [
    {
      name: "field",
      label: { key: "entity-data.field" },
      getValue: (row) => row.name,
      align: "left",
    },
    {
      name: "value",
      label: { key: "entity-data.value" },
      getValue: (row) => <RowValue row={row} />,
      align: "left",
    },
    ...(capturesValueDates(rows)
      ? customFieldDateValidations.map(
          (validation): TableColumn<EntityFieldRow> => ({
            name: validation,
            label: valueDateLabels[validation],
            getValue: (row) => <RowDate row={row} validation={validation} />,
            align: "left",
          }),
        )
      : []),
    {
      name: "lastUpdated",
      label: { key: "entity-data.last-updated" },
      getValue: (row) =>
        row.updatedAt
          ? t("entity-data.updated-by", {
              date: formatDate(row.updatedAt),
              name: row.updatedBy ?? "",
            })
          : "",
      align: "left",
    },
    {
      name: "info",
      label: "",
      getValue: (row) => (
        <Box justifyContent={"end"}>
          <InfoButton infoText={row.description} />
        </Box>
      ),
    },
    {
      name: "actions",
      label: "",
      stopClick: true,
      getValue: (row) =>
        row.origin === "SYSTEM" ? null : (
          <Box justifyContent={"end"}>
            <DropdownMenu
              button={{ type: "dotdotdot" }}
              items={[
                row.field.canEdit && {
                  type: "item",
                  icon: "note-pencil",
                  label: { key: "entity-data.edit-value" },
                  onSelect: () => onEditValue(row.field),
                },
                {
                  type: "item",
                  icon: "clock-counter-clockwise",
                  label: { key: "entity-data.history" },
                  onSelect: () => onShowHistory(row.field),
                },
              ]}
            />
          </Box>
        ),
    },
  ];
  return (
    <DataTable
      tableColumns={columns}
      tableData={rows}
      getId={(row) => row.id}
      onRowClick={onSelectField}
      rowProps={(row) => ({ selected: row.id === fieldPanel?.id })}
    />
  );
});

const EntityFieldPanel: FC<{
  panel: EntityFieldPanelViewProps;
  onEditValue: (field: CompanyCustomField) => void;
  onConfigureFields?: () => void;
}> = observer(({ panel, onEditValue, onConfigureFields }) => {
  const { t } = useTranslation();
  const configureField = panel.origin === "CUSTOM" ? panel.onConfigureField : undefined;
  const configure: { label: TMessage; onSelect: () => void } | undefined = configureField
    ? { label: { key: "entity-data.edit-field" }, onSelect: configureField }
    : onConfigureFields
      ? { label: { key: "entity-data.configure-fields" }, onSelect: onConfigureFields }
      : undefined;
  return (
    <>
      <VBox borderBottom paddingTop={"lg"} paddingX={"md"} gap={"md"}>
        <Box justifyContent={"space-between"} alignItems={"center"} minHeight={"md"}>
          <h4>{panel.name}</h4>
          <Box alignItems={"center"}>
            {configure && (
              <DropdownMenu
                button={{ type: "link", label: { key: "options" } }}
                items={[{ type: "item", icon: "gear", ...configure }]}
              />
            )}
            <CloseButton onClick={panel.onClose} />
          </Box>
        </Box>
        <Tabs
          tabs={[
            {
              id: "field",
              label: t("entity-data.field"),
              isActive: panel.tab === "field",
              onSelect: () => panel.onSelectTab("field"),
            },
            {
              id: "log",
              label: t("entity-data.tab-log"),
              isActive: panel.tab === "log",
              disabled: panel.origin === "SYSTEM" && !panel.hasLog,
              onSelect: () => panel.onSelectTab("log"),
            },
          ]}
        />
      </VBox>
      <VBox padding gap={"lg"} grow overflowY={"auto"}>
        {panel.origin === "SYSTEM" ? (
          <>
            {panel.tab === "field" && <EntitySystemPanelField panel={panel} />}
            {panel.tab === "log" && <EntitySystemPanelLog panel={panel} />}
          </>
        ) : (
          <>
            {panel.tab === "field" && <EntityPanelField panel={panel} onEditValue={onEditValue} />}
            {panel.tab === "log" && <EntityPanelLog panel={panel} />}
          </>
        )}
      </VBox>
    </>
  );
});

const scopeLabels: { [key in CustomFieldCompanyScope]: TMessageKey } = {
  ALL_COMPANIES: "all-companies",
  SELECTED_COMPANIES: "entity-data.selected-companies",
  HIDDEN: "hidden",
};

type CustomPanelProps = Extract<EntityFieldPanelViewProps, { origin: "CUSTOM" }>;

type SystemPanelProps = Extract<EntityFieldPanelViewProps, { origin: "SYSTEM" }>;

const EntitySystemPanelField: FC<{ panel: SystemPanelProps }> = observer(({ panel }) => {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  const { effectiveFrom, updatedAt } = panel.field;
  return (
    <VBox gap={"lg"}>
      <Grid cols={2} gap={"md"}>
        <Box fg={"subtle"} alignItems={"center"}>
          {t("entity-data.value")}
        </Box>
        <Box alignItems={"center"} justifyContent={"space-between"} gap={"md"}>
          <SystemFieldValue field={panel.field} />
          {panel.onEdit && (
            <Button variant={"secondary"} icon={"edit"} label={t("edit")} onClick={panel.onEdit} />
          )}
        </Box>
        {effectiveFrom && (
          <>
            <Box fg={"subtle"}>{t("entity-data.effective-from")}</Box>
            <div>{formatDate(effectiveFrom)}</div>
          </>
        )}
        {updatedAt && (
          <>
            <Box fg={"subtle"}>{t("entity-data.last-updated")}</Box>
            <div>{formatDate(updatedAt)}</div>
          </>
        )}
        <Box fg={"subtle"}>{t("type")}</Box>
        <div>{t("entity-data.system-field")}</div>
        {panel.description && (
          <>
            <Box fg={"subtle"}>{t("entity-data.description")}</Box>
            <div>{panel.description}</div>
          </>
        )}
      </Grid>
      {(panel.detailRows.length > 0 || panel.detailLink) && (
        <>
          <Box className={"font-semibold"}>{t("entity-data.field-details")}</Box>
          <Grid cols={2} gap={"md"}>
            {panel.detailRows.map((row) => (
              <Fragment key={row.id}>
                <Box fg={"subtle"}>{t(row.label)}</Box>
                <div>{t(row.value)}</div>
              </Fragment>
            ))}
          </Grid>
          {panel.detailLink && (
            <div>
              <LinkButton
                label={panel.detailLink.label}
                arrow={"right"}
                onClick={panel.detailLink.onClick}
              />
            </div>
          )}
        </>
      )}
    </VBox>
  );
});

const EntityPanelField: FC<{
  panel: CustomPanelProps;
  onEditValue: (field: CompanyCustomField) => void;
}> = observer(({ panel, onEditValue }) => {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  const field = panel.field;
  return (
    <VBox gap={"lg"}>
      <Box justifyContent={"space-between"} alignItems={"start"} gap={"md"}>
        <Grid cols={2} gap={"md"} grow>
          <Box className={"font-semibold"}>{t("entity-data.current-value")}</Box>
          <FieldValue
            value={field.value}
            selectOptions={field.selectOptions}
            numberFormat={field.numberFormat}
          />
          {dateValidationEntries(field.validations).map((entry) => (
            <Fragment key={entry.validation}>
              <Box fg={"subtle"}>{t(valueDateLabels[entry.validation])}</Box>
              <ValueDate date={valueDate(field, entry.validation)} />
            </Fragment>
          ))}
          {field.validations?.COMMENT && (
            <>
              <Box fg={"subtle"}>{t("entity-data.comment")}</Box>
              {field.comment ? (
                <div>{field.comment}</div>
              ) : (
                <Box fg={"subtle"}>{t("entity-data.not-set")}</Box>
              )}
            </>
          )}
        </Grid>
        {field.canEdit && (
          <Button
            variant={"secondary"}
            icon={"edit"}
            label={t("edit")}
            onClick={() => onEditValue(field)}
          />
        )}
      </Box>
      <Box className={"font-semibold"}>{t("entity-data.field-details")}</Box>
      <Grid cols={2} gap={"md"}>
        {field.updatedAt && (
          <>
            <Box fg={"subtle"}>{t("entity-data.last-updated")}</Box>
            <div>{formatDate(field.updatedAt)}</div>
          </>
        )}
        {field.updatedBy && (
          <>
            <Box fg={"subtle"}>{t("entity-data.last-updated-by")}</Box>
            <div>{field.updatedBy}</div>
          </>
        )}
        <Box fg={"subtle"}>{t("type")}</Box>
        <div>{t(dataTypeLabels[field.dataType])}</div>
        {field.description && (
          <>
            <Box fg={"subtle"}>{t("entity-data.description")}</Box>
            <div>{field.description}</div>
          </>
        )}
        {field.companyScope && (
          <>
            <Box fg={"subtle"}>{t("entity-data.activated-in")}</Box>
            <div>{t(scopeLabels[field.companyScope])}</div>
          </>
        )}
        {field.dataType === "SINGLE_SELECT" && (
          <>
            <Box fg={"subtle"}>{t("entity-data.tab-values")}</Box>
            <VBox gap={"xs"}>
              {field.selectOptions
                .filter((option) => !option.isArchived)
                .map((option) => (
                  <div key={option.id}>{option.label}</div>
                ))}
            </VBox>
          </>
        )}
      </Grid>
    </VBox>
  );
});

const EntitySystemPanelLog: FC<{ panel: SystemPanelProps }> = observer(({ panel }) => {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  const columns: TableColumn<SystemFieldLogRow>[] = [
    {
      name: "date",
      label: { key: "date" },
      getValue: (row) => formatDate(row.date),
      align: "left",
    },
    {
      name: "change",
      label: { key: "entity-data.log-change" },
      getValue: (row) => t(row.change),
      align: "left",
    },
    {
      name: "by",
      label: { key: "entity-data.log-by" },
      getValue: (row) => row.actorName,
      align: "left",
    },
  ];
  return (
    <>
      {isPending(panel.logResult) && <LoadingIndicator />}
      {isFailure(panel.logResult) && <Alert type={"error"} message={t("general-error")} />}
      {isOk(panel.logResult) && (
        <DataTable tableColumns={columns} tableData={panel.logRows} getId={(row) => row.id} />
      )}
    </>
  );
});

const EntityPanelLog: FC<{ panel: CustomPanelProps }> = observer(({ panel }) => {
  const { t } = useTranslation();
  const { formatDateTime } = useLocaleFormat();
  return (
    <>
      {isPending(panel.logResult) && <LoadingIndicator />}
      {isFailure(panel.logResult) && <Alert type={"error"} message={t("general-error")} />}
      {isOk(panel.logResult) && (
        <VBox gap={"md"}>
          {panel.logRows.map((row) => (
            <Box key={row.id} gap={"md"} justifyContent={"space-between"} alignItems={"start"}>
              <VBox>
                <div>{t(row.label)}</div>
                {row.value && (
                  <Box textSize={"sm"}>
                    <FieldValue
                      value={row.value}
                      selectOptions={panel.field.selectOptions}
                      numberFormat={panel.field.numberFormat}
                    />
                  </Box>
                )}
                <small className={"subtle"}>{row.actorName}</small>
              </VBox>
              <Box fg={"subtle"}>{formatDateTime(row.recordedAt)}</Box>
            </Box>
          ))}
        </VBox>
      )}
    </>
  );
});

const createFieldTitles: { [key in CustomFieldDataType]: TMessageKey } = {
  TEXT: "entity-data.create-text-field",
  NUMBER: "entity-data.create-number-field",
  DATE: "entity-data.create-date-field",
  SINGLE_SELECT: "entity-data.create-select-field",
  COUNTRY_CODE: "entity-data.create-country-field",
  CONTACT: "entity-data.create-contact-field",
};

export const AddFieldDialog: FC<{
  model: AddFieldFormModel;
  accountName: string;
  onSubmitAddField: () => void;
  onCloseAddField: () => void;
  onDeleteField?: () => void;
}> = observer(({ model, accountName, onSubmitAddField, onCloseAddField, onDeleteField }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t(
        model.definition ? "entity-data.edit-field-title" : createFieldTitles[model.dataType],
      )}
      onCancel={onCloseAddField}
      onSubmit={onSubmitAddField}
      isSubmitDisabled={!model.isValid}
      isSubmitting={model.isSubmitting}
      submitLabel={t("save")}
      maxWidth={"sm"}
      actions={
        onDeleteField && (
          <Box justifyContent={"space-between"} alignItems={"center"} gap={"xl"}>
            <Box>
              <LinkButton
                icon={"trash"}
                label={{ key: "entity-data.delete-field" }}
                color={"error"}
                weight={"normal"}
                onClick={onDeleteField}
              />
            </Box>
            <Box gap={"sm"}>
              <Button variant={"secondary"} label={t("cancel")} onClick={onCloseAddField} />
              <Button
                variant={"primary"}
                label={t("save")}
                loading={model.isSubmitting}
                disabled={!model.isValid}
                onClick={onSubmitAddField}
              />
            </Box>
          </Box>
        )
      }
    >
      <VBox gap={"lg"}>
        <Tabs
          tabs={[
            {
              id: "setup",
              label: t("entity-data.tab-setup"),
              isActive: model.activeTab === "setup",
              onSelect: () => model.setActiveTab("setup"),
            },
            {
              id: "validation",
              label: t("entity-data.tab-validation"),
              isActive: model.activeTab === "validation",
              onSelect: () => model.setActiveTab("validation"),
            },
            {
              id: "permissions",
              label: t("entity-data.tab-permissions"),
              isActive: model.activeTab === "permissions",
              onSelect: () => model.setActiveTab("permissions"),
            },
            {
              id: "activation",
              label: t("entity-data.tab-activation"),
              isActive: model.activeTab === "activation",
              onSelect: () => model.setActiveTab("activation"),
            },
          ]}
        />
        {model.activeTab === "setup" && <AddFieldSetup model={model} />}
        {model.activeTab === "validation" && <AddFieldValidation model={model} />}
        {model.activeTab === "permissions" && (
          <AddFieldPermissions model={model} accountName={accountName} />
        )}
        {model.activeTab === "activation" && (
          <AddFieldActivation model={model} accountName={accountName} />
        )}
        {model.requiredLengthIssue && (
          <Alert
            type={model.requiredLengthIssue.level}
            icon
            message={t(model.requiredLengthIssue.message)}
          />
        )}
        {model.error && <Alert type={"error"} message={t(model.error)} />}
      </VBox>
    </Dialog>
  );
});

const AddFieldSetup: FC<{ model: AddFieldFormModel }> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"lg"}>
      <MTextInput model={model.title} label={t("entity-data.field-title")} autofocus />
      {model.dataType === "NUMBER" && <AddFieldFormatting model={model} />}
      {model.showDescription ? (
        <MTextArea model={model.description} label={t("entity-data.description")} />
      ) : (
        <Box>
          <LinkButton
            icon={"plus"}
            label={{ key: "entity-data.add-description" }}
            onClick={model.addDescription}
          />
        </Box>
      )}
      {model.dataType === "SINGLE_SELECT" && (
        <VBox gap={"sm"}>
          <Box className={"font-semibold"}>{t("entity-data.options")}</Box>
          <SortableList items={model.options} onMove={model.moveOption}>
            {model.options.map((option) => (
              <Sortable key={option.id} id={option.id}>
                {(dragProps) => (
                  <Box alignItems={"center"} gap={"sm"} paddingY={"xs"}>
                    <Box
                      fg={"subtle"}
                      htmlProps={{ ...dragProps.dragAttributes, ...dragProps.dragListeners }}
                    >
                      <Icon name={"dots-six-vertical"} />
                    </Box>
                    <Box flex={"auto"}>
                      <MTextInput
                        model={option.label}
                        autofocus={option.id === model.focusOptionId}
                      />
                    </Box>
                    <IconButton
                      icon={"trash"}
                      color={"error"}
                      tooltip={{ key: "remove" }}
                      onClick={() => model.removeOption(option.id)}
                    />
                  </Box>
                )}
              </Sortable>
            ))}
          </SortableList>
          <Box>
            <LinkButton
              icon={"plus"}
              label={{ key: "entity-data.add-option" }}
              onClick={model.addOption}
            />
          </Box>
        </VBox>
      )}
    </VBox>
  );
});

const FormatOption: FC<{ label: string; sample: string }> = ({ label, sample }) => (
  <Box grow justifyContent={"space-between"} gap={"lg"}>
    <span>{label}</span>
    <Box fg={"subtle"}>{sample}</Box>
  </Box>
);

const AddFieldFormatting: FC<{ model: AddFieldFormModel }> = observer(({ model }) => {
  const { t } = useTranslation();
  const lang = useLanguage();
  const separators = model.numberFormat?.separators;
  return (
    <VBox gap={"md"}>
      <div>{t("entity-data.formatting")}</div>
      <MSelect
        model={model.decimalPlaces}
        label={t("entity-data.decimal-places")}
        fullWidth
        itemLabelFn={(places) => (
          <FormatOption
            label={String(places)}
            sample={formatCustomNumber(lang, 1, { decimalPlaces: places, separators })}
          />
        )}
      />
      <MCheckbox
        model={model.thousandsSeparator}
        label={t("entity-data.thousands-separator")}
        descriptionText={formatCustomNumber(lang, 1000000, {
          decimalPlaces: model.decimalPlaces.value,
          separators,
        })}
      />
    </VBox>
  );
});

const AddFieldValidation: FC<{ model: AddFieldFormModel }> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <div>{t("entity-data.validation-intro")}</div>
      <VBox gap={"md"}>
        {model.validationRows.map((row) => (
          <Box key={row.id} alignItems={"center"} gap={"md"}>
            <Box flex={"auto"}>
              <MSelect model={row.validation} fullWidth />
            </Box>
            <Box flex={"auto"}>
              {row.validation.value === "REQUIRED_LENGTH" ? (
                <MTextInput
                  model={model.requiredLength}
                  align={"right"}
                  addon={
                    <Box fg={"subtle"} paddingX={"md"}>
                      {t("entity-data.digits")}
                    </Box>
                  }
                  addonPosition={"right"}
                />
              ) : row.validation.value === "LIMIT_SELECTION_TO_ROLE" ? (
                <MSelect
                  model={model.limitToRole}
                  placeholder={t("entity-data.select-user-group")}
                  fullWidth
                />
              ) : (
                <MSelect model={row.requirement} fullWidth />
              )}
            </Box>
            <IconButton
              icon={"trash"}
              color={"error"}
              tooltip={{ key: "remove" }}
              onClick={() => model.removeValidationRow(row.id)}
            />
          </Box>
        ))}
      </VBox>
      {model.isCommentConditional && (
        <VBox gap={"sm"}>
          <div>{t("entity-data.comment-condition")}</div>
          <MTextInput model={model.commentRequiredFor} />
          <Box fg={"subtle"} textSize={"sm"}>
            {t("entity-data.comment-condition-hint")}
          </Box>
        </VBox>
      )}
      <Box>
        <DropdownMenu
          button={{ type: "link", icon: "plus", label: { key: "entity-data.add-rule" } }}
          disabled={model.unusedValidations.length === 0}
          items={model.unusedValidations.map((validation) => ({
            type: "item",
            icon: validationIcons[validation],
            label: validationLabels[validation],
            onSelect: () => model.addValidationRow(validation),
          }))}
        />
      </Box>
    </VBox>
  );
});

const AddFieldPermissions: FC<{ model: AddFieldFormModel; accountName: string }> = observer(
  ({ model, accountName }) => {
    const { t } = useTranslation();
    const rows = [
      {
        key: "account-admin",
        role: t("enterprise-account.account-admin"),
        model: model.accountAdminPermission,
        disabled: true,
      },
      { key: "admin", role: t("administrators"), model: model.adminPermission, disabled: false },
      {
        key: "board-member",
        role: t("entity-data.role-board-members"),
        model: model.boardMemberPermission,
        disabled: false,
      },
      {
        key: "shareholder",
        role: t("entity-data.role-shareholders"),
        model: model.shareholderPermission,
        disabled: false,
      },
    ];
    return (
      <VBox gap={"md"}>
        <div>{t("entity-data.permissions-intro", { accountName })}</div>
        <Box className={"font-semibold"}>{t("entity-data.permissions-per-role")}</Box>
        <Grid cols={2} gap={"md"}>
          <Box fg={"subtle"}>{t("role")}</Box>
          <Box fg={"subtle"}>{t("entity-data.access")}</Box>
          {rows.map((row) => (
            <Fragment key={row.key}>
              <Box alignItems={"center"}>{row.role}</Box>
              <MSelect model={row.model} disabled={row.disabled} />
            </Fragment>
          ))}
        </Grid>
      </VBox>
    );
  },
);

const AddFieldActivation: FC<{ model: AddFieldFormModel; accountName: string }> = observer(
  ({ model, accountName }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"md"}>
        <div>{t("entity-data.activation-intro", { accountName })}</div>
        <Box className={"font-semibold"}>{t("entity-data.activate-for-companies")}</Box>
        <MRadioButtonGroup model={model.companyScope} horizontal />
        {model.companyScope.value === "SELECTED_COMPANIES" && (
          <VBox gap={"md"}>
            <Box gap={"xs"}>
              <LinkButton
                label={{ key: "select-all" }}
                onClick={model.companySelection.selectAll}
              />
              <span>/</span>
              <LinkButton label={{ key: "none" }} onClick={model.companySelection.clear} />
            </Box>
            <Box overflowY={"auto"} className={"max-h-80"}>
              <MCheckboxGroup model={model.companySelection} />
            </Box>
          </VBox>
        )}
      </VBox>
    );
  },
);

const EntityValueInput: FC<{ model: EntityValueFormModel; label?: string }> = observer(
  ({ model, label }) => {
    switch (model.field.dataType) {
      case "TEXT":
        return <MTextInput model={model.text} label={label} />;
      case "NUMBER":
        return <MTextInput model={model.number} label={label} />;
      case "DATE":
        return <MDateInput model={model.date} label={label} />;
      case "SINGLE_SELECT":
        return <MSelect model={model.option} label={label} fullWidth />;
      case "COUNTRY_CODE":
        return <MSelect model={model.country} label={label} fullWidth />;
      case "CONTACT":
        return <ContactInput model={model} label={label} />;
    }
  },
);

const ContactInput: FC<{ model: EntityValueFormModel; label?: string }> = observer(
  ({ model, label }) => {
    const { t } = useTranslation();
    return (
      <Autocomplete
        label={label}
        placeholder={t(contactSearchPlaceholder(model.field))}
        items={model.contacts}
        value={model.contact}
        loading={model.isLoadingContacts}
        idFn={(contact) => String(contact.userId)}
        labelFn={(contact) => contact.name}
        onSelect={model.setContact}
        onClear={() => model.setContact(null)}
        fullWidth
      />
    );
  },
);

export const EntityValueFields: FC<{
  model: EntityValueFormModel;
  label?: string;
  companyName?: string;
}> = observer(({ model, label, companyName }) => {
  const { t } = useTranslation();
  const field = model.field;
  const contactNote = contactWarning(field, model.contact, companyName);
  return (
    <VBox gap={"md"}>
      <EntityValueInput model={model} label={label} />
      {dateValidationEntries(field.validations).length > 0 && (
        <Box gap={"md"}>
          {dateValidationEntries(field.validations).map((entry) => (
            <Box key={entry.validation} flex={"auto"}>
              <MDateInput
                model={model.dateModel(entry.validation)}
                label={t(valueDateFieldLabel(entry))}
              />
            </Box>
          ))}
        </Box>
      )}
      {field.validations?.COMMENT && (
        <MTextArea
          model={model.comment}
          label={t("entity-data.comment")}
          placeholder={t(model.isCommentRequired ? "required" : "optional")}
          maxLength={commentMaxLength}
        />
      )}
      {contactNote && <Alert type={"warning"} icon message={contactNote} />}
    </VBox>
  );
});

export const EditValueDialog: FC<{
  model: EntityValueFormModel;
  companyName?: string;
  subjectName?: string;
  onSubmit: () => void;
  onCancel: () => void;
}> = observer(({ model, companyName, subjectName, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const field = model.field;
  const warning = overlapWarning(field, model.toInput());
  const titleName = subjectName ?? companyName;
  return (
    <Dialog
      title={t(
        titleName
          ? {
              key: "entity-data.update-value-title",
              params: { field: field.name, companyName: titleName },
            }
          : { key: "entity-data.update-field-value-title", params: { field: field.name } },
      )}
      onCancel={onCancel}
      onSubmit={onSubmit}
      isSubmitDisabled={!model.isValid}
      isSubmitting={model.isSubmitting}
      submitLabel={t("save")}
      maxWidth={"sm"}
    >
      <VBox gap={"md"}>
        <Box gap={"xs"}>
          <Box fg={"subtle"}>{t("entity-data.current-value-colon")}</Box>
          <FieldValue
            value={field.value}
            selectOptions={field.selectOptions}
            numberFormat={field.numberFormat}
          />
        </Box>
        <EntityValueFields
          model={model}
          label={t("entity-data.new-value")}
          companyName={companyName}
        />
        {warning && <Alert type={"warning"} icon message={warning} />}
        {model.error && <Alert type={"error"} message={t(model.error)} />}
      </VBox>
    </Dialog>
  );
});

export const ValueHistoryDialog: FC<{
  history: EntityDataHistoryState;
  title?: string;
  onClose: () => void;
}> = observer(({ history, title, onClose }) => {
  const { t } = useTranslation();
  const { formatDateTime } = useLocaleFormat();
  return (
    <Dialog
      title={title ?? t("entity-data.history-title", { name: history.field.name })}
      onCancel={onClose}
      cancelLabel={t("close")}
      maxWidth={"md"}
    >
      {isPending(history.result) && <LoadingIndicator />}
      {isFailure(history.result) && <Alert type={"error"} message={t("general-error")} />}
      {isOk(history.result) && (
        <VBox gap={"md"}>
          {history.result.data.map((entry) => (
            <Box key={entry.id} gap={"md"} justifyContent={"space-between"} alignItems={"start"}>
              <VBox>
                <div>
                  {entry.payload.payloadType === "VALUE_SET" ? (
                    <FieldValue
                      value={entry.payload.value}
                      selectOptions={history.field.selectOptions}
                    />
                  ) : (
                    entry.eventType
                  )}
                </div>
                <small className={"subtle"}>{entry.actor.name}</small>
              </VBox>
              <Box fg={"subtle"}>{formatDateTime(entry.recordedAt)}</Box>
            </Box>
          ))}
        </VBox>
      )}
    </Dialog>
  );
});

export const ImportResultDialog: FC<{
  result: CustomFieldsImportResult;
  onClose: () => void;
}> = observer(({ result, onClose }) => {
  const { t } = useTranslation();
  const outcome = importOutcome(result);
  const counts: { key: string; label: TMessage; value: number }[] = [
    { key: "updated", label: { key: "entity-data.import-updated" }, value: result.updated },
    { key: "cleared", label: { key: "entity-data.import-cleared" }, value: result.cleared },
    { key: "unchanged", label: { key: "entity-data.import-unchanged" }, value: result.unchanged },
  ];
  const issueColumns: TableColumn<CustomFieldImportIssue>[] = [
    {
      name: "row",
      label: { key: "entity-data.import-row" },
      getValue: (issue) => issue.row ?? "",
      align: "left",
    },
    {
      name: "column",
      label: { key: "entity-data.field-name" },
      getValue: (issue) => issue.column ?? "",
      align: "left",
    },
    {
      name: "problem",
      label: { key: "entity-data.import-reason" },
      getValue: (issue) => t(importProblemLabels[issue.problem]),
      align: "left",
    },
    {
      name: "value",
      label: { key: "entity-data.value" },
      getValue: (issue) => issue.value ?? "",
      align: "left",
    },
  ];
  return (
    <Dialog
      title={t("entity-data.import-result-title")}
      onCancel={onClose}
      cancelLabel={t("close")}
      maxWidth={"md"}
    >
      <VBox gap={"lg"}>
        {outcome !== "changed" && (
          <Alert type={"warning"} message={t(importOutcomeWarnings[outcome])} />
        )}
        <Grid cols={2} gap={"md"}>
          {counts.map((count) => (
            <Fragment key={count.key}>
              <Box fg={"subtle"}>{t(count.label)}</Box>
              <div>{count.value}</div>
            </Fragment>
          ))}
        </Grid>
        {result.issues.length === 0 && outcome === "changed" && (
          <Alert type={"success"} message={t("entity-data.import-no-problems")} />
        )}
        {result.issues.length > 0 && (
          <VBox gap={"md"}>
            <Box className={"font-semibold"}>{t("entity-data.import-problems")}</Box>
            <DataTable
              tableColumns={issueColumns}
              tableData={result.issues}
              getId={(issue) => `${issue.row ?? 0}-${issue.column ?? ""}-${issue.problem}`}
            />
          </VBox>
        )}
      </VBox>
    </Dialog>
  );
});
